import React, { useRef } from 'react';
import { LayoutGrid } from 'lucide-react';
import CollageFeatureIcon from './CollageFeatureIcon';
import useFitTextToWidth from '../hooks/useFitTextToWidth';
import { COLLAGE_MAIN_PHOTO_SHAPES, COLLAGE_TITLE_MIN_FONT_PX } from '../constants/bookingConst';
import '../css/BookingGalleryCollage.css';

/**
 * A CSS mask image of an SVG path in a 0-100 box, stretched to whatever it
 * masks (preserveAspectRatio none).
 *
 * @param {string} pathData - SVG path data in 0-100 coordinates
 * @returns {string} a `url("data:image/svg+xml,…")` value for `mask-image`
 */
const buildShapeMask = (pathData) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><path d='${pathData}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

// Both breakpoints' masks are handed to the stylesheet as custom properties;
// the CSS picks one per screen size (see .bgc-collage / .bgc-main).
const MAIN_PHOTO_MASK_VARS = {
  '--bgc-main-mask-wide': buildShapeMask(COLLAGE_MAIN_PHOTO_SHAPES.wide),
  '--bgc-main-mask-narrow': buildShapeMask(COLLAGE_MAIN_PHOTO_SHAPES.narrow),
};

/**
 * Splits a club name into the plain lead word and the highlighted remainder
 * the collage title sets on its own line — "Paddle" / "Point Club".
 *
 * A one-word name has nothing to highlight and comes back whole as the lead,
 * so the title never shows an empty accent line.
 *
 * @param {string} name - the club name (Config -> courtName)
 * @returns {{lead: string, accent: string}}
 */
const splitClubName = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { lead: words.join(' '), accent: '' };
  return { lead: words[0], accent: words.slice(1).join(' ') };
};

/**
 * The framed-window photo collage at the top of the guest booking screen:
 * an info panel (eyebrow, club name, a few short facts), one large photo and
 * up to three stacked thumbnails, with a "Show all photos" button.
 *
 * Collapses gracefully: no thumbnails drops the third column, and no photos
 * at all leaves just the info panel, so a club that hasn't uploaded a gallery
 * still gets its name and facts in the same frame.
 *
 * @param {object} props
 * @param {{id?: string, url: string}[]} props.photos - the photos to draw, main first (at most 4)
 * @param {number} props.totalPhotos - every gallery photo, for whether "Show all photos" is worth showing
 * @param {(index: number) => void} props.onOpenPhoto - opens the lightbox at that photo
 * @param {string} [props.eyebrow] - small uppercase line above the name
 * @param {string} props.clubName
 * @param {{key: string, icon: string, label: string}[]} [props.features] - short
 *   facts listed under the name; `icon` names a CollageFeatureIcon
 * @returns {JSX.Element}
 */
function BookingGalleryCollage({ photos = [], totalPhotos = 0, onOpenPhoto, eyebrow, clubName, features = [] }) {
  const [mainPhoto, ...sidePhotos] = photos;
  const { lead, accent } = splitClubName(clubName);
  const layoutClass = !mainPhoto ? ' no-photos' : sidePhotos.length === 0 ? ' no-thumbs' : '';
  const titleRef = useRef(null);
  // Shrinks the name rather than letting a long word ("Pickcoolbowl") break
  // mid-word onto a second line in the narrow info panel.
  useFitTextToWidth(titleRef, '.bgc-title-line', COLLAGE_TITLE_MIN_FONT_PX, [lead, accent]);

  return (
    <div className={`bgc-collage${layoutClass}`} style={MAIN_PHOTO_MASK_VARS}>
      <div className="bgc-info">
        {eyebrow && <div className="bgc-eyebrow">{eyebrow}</div>}
        <h1 className="bgc-title" ref={titleRef}>
          <span className="bgc-title-line">{lead}</span>
          {accent && <>{' '}<span className="bgc-title-line bgc-title-accent">{accent}</span></>}
        </h1>
        {features.length > 0 && (
          <ul className="bgc-features">
            {features.map(feature => (
              <li key={feature.key} className="bgc-feature">
                <CollageFeatureIcon name={feature.icon} />
                <span className="bgc-feature-text">{feature.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {mainPhoto && (
        <button type="button" className="bgc-tile bgc-main" onClick={() => onOpenPhoto(0)} aria-label="View photo 1">
          <img src={mainPhoto.url} alt="Court view 1" />
          {/* The photo's border. A CSS border can't follow the masked, slanted
              edge, so the same shape is stroked on top instead — one path per
              breakpoint, the CSS shows the one in use. */}
          <svg className="bgc-main-outline" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <path className="bgc-main-outline-wide" d={COLLAGE_MAIN_PHOTO_SHAPES.wide} />
            <path className="bgc-main-outline-narrow" d={COLLAGE_MAIN_PHOTO_SHAPES.narrow} />
          </svg>
        </button>
      )}

      {sidePhotos.length > 0 && (
        <div className="bgc-thumbs" style={{ '--bgc-thumb-count': sidePhotos.length }}>
          {sidePhotos.map((photo, i) => (
            <button
              key={photo.id || photo.url}
              type="button"
              className="bgc-tile"
              onClick={() => onOpenPhoto(i + 1)}
              aria-label={`View photo ${i + 2}`}
            >
              <img src={photo.url} alt={`Court view ${i + 2}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {totalPhotos > 1 && (
        <button type="button" className="gallery-showall bgc-showall" onClick={() => onOpenPhoto(0)}>
          <LayoutGrid size={16} />
          <span>Show all photos</span>
        </button>
      )}
    </div>
  );
}

export default BookingGalleryCollage;
